import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useEventManager } from '../events/hooks/useEventManager';
import { useSimpleEventOrganization } from '../events/hooks/useSimpleEventOrganization';
import { useSettings } from '../../contexts/settingsContext';
import { useDialogState } from '../events/hooks/useDialogState';
import { useHistoryDrawer } from '../../contexts/historyDrawerContext';
import { currentAuthenticatedUser } from '../../services/auth/authService';
import HistoryDrawer from '../events/list/HistoryDrawer';
import HistoryDialogs from '../events/dialogs/HistoryDialogs';
import CreateStatementDialog from '../statements/CreateStatementDialog';

/**
 * HistoryManager - Main component for event history management
 * Refactored for better maintainability and separation of concerns
 */
export default function HistoryManager({ open, onClose }) {
  // Event management
  const {
    allEvents,
    setAllEvents, // For WebSocket real-time updates
    openAssignments,
    loading,
    loadingMore,
    hasMore,
    error,
    setError, // For WebSocket error handling
    success,
    pageSize,
    loadAllEvents,
    loadMoreEvents,
    changePageSize,
    createNewAssignment,
    toggleAssignment,
    createNewNote,
    updateEventDescription,
    deleteEventById,
    clearError,
    clearSuccess
  } = useEventManager();

  // WebSocket message handler - matches aggregator pattern
  const handleWebSocketMessage = useCallback((message) => {
    if (message.type !== 'EVENT_UPDATE') return;

    const { operation, data: event } = message;
    if (!event) return;

    console.log('[HistoryManager WebSocket] Processing event:', {
      operation,
      eventId: event.eventId,
      eventType: event.eventType,
      personName: event.metadata?.personName,
      statementCount: event.metadata?.statementCount
    });

    setAllEvents(prevEvents => {
      const existingIndex = prevEvents.findIndex(e => e.eventId === event.eventId);

      if (operation === 'REMOVE') {
        if (existingIndex === -1) return prevEvents;
        return prevEvents.filter((_, index) => index !== existingIndex);
      }

      if (operation === 'MODIFY' && existingIndex !== -1) {
        const updated = [...prevEvents];
        updated[existingIndex] = event;
        return updated;
      }

      if (existingIndex !== -1) return prevEvents;
      return [event, ...prevEvents];
    });
  }, [setAllEvents]);

  // WebSocket connection - stays connected even when drawer closed
  const websocketEnabled = import.meta.env.VITE_WEBSOCKET_ENABLED === 'true';
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);

  const connectWebSocket = useCallback(async () => {
    if (!websocketEnabled) return;

    try {
      // Close existing connection if any
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      const user = await currentAuthenticatedUser();
      const token = user?.signInUserSession?.idToken?.jwtToken;
      if (!token) return;

      const wsUrl = import.meta.env.VITE_WEBSOCKET_URL;
      const ws = new WebSocket(`${wsUrl}?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[HistoryManager] WebSocket connected');
        setWsConnected(true);
      };

      ws.onclose = () => {
        console.log('[HistoryManager] WebSocket disconnected');
        setWsConnected(false);
      };

      ws.onerror = (err) => {
        console.error('[HistoryManager] WebSocket error:', err);
        setWsConnected(false);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.action === 'pong') return;
          handleWebSocketMessage(message);
        } catch (err) {
          console.error('WebSocket parse error:', err);
        }
      };
    } catch (err) {
      console.error('WebSocket connection failed:', err);
      setWsConnected(false);
    }
  }, [websocketEnabled, handleWebSocketMessage]);

  useEffect(() => {
    connectWebSocket();

    return () => {
      // Don't close WebSocket on unmount - keep connection alive like aggregator
      // if (wsRef.current) wsRef.current.close();
    };
  }, [connectWebSocket]);

  // Share open assignments with global context for PersonCard
  const { updateOpenAssignments, pendingMatchEvents, removePendingMatchEvent } = useHistoryDrawer();
  useEffect(() => {
    updateOpenAssignments(openAssignments);
  }, [openAssignments, updateOpenAssignments]);

  // Merge pending match events with real events
  const mergedEvents = React.useMemo(() => {
    // Remove pending events that now exist as real events
    // 5000ms window: Treat events as duplicates if they have same person/eventId and occur within 5 seconds
    const DUPLICATE_DETECTION_WINDOW_MS = 5000;


    const validPending = pendingMatchEvents.filter(pe => {
      // For group events, check by eventId
      if (pe.eventType === 'group_match') {
        return !allEvents.some(e => e.eventId === pe.eventId);
      }

      // For match events (known and SOI), check by person ID and time window
      // Use person_id if available (more precise), fallback to personName
      const hasDuplicate = allEvents.some(e => {
        if (e.eventType !== 'match' && e.eventType !== 'soi_match') return false;

        // Match by person_id if both have it
        const pendingPersonId = pe.metadata?.person?.person_id;
        const realPersonId = e.metadata?.person?.person_id;

        if (pendingPersonId && realPersonId) {
          if (pendingPersonId !== realPersonId) return false;
        } else {
          // Fallback to personName if person_id not available
          if (e.metadata?.personName !== pe.metadata?.personName) return false;
        }

        const timeDiff = Math.abs(new Date(e.createdAt).getTime() - new Date(pe.createdAt).getTime());

        // Match found: same person, same time window
        return timeDiff < DUPLICATE_DETECTION_WINDOW_MS;
      });

      return !hasDuplicate;
    });


    const merged = [...validPending, ...allEvents].sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );


    return merged;
  }, [allEvents, pendingMatchEvents]);

  // Clean up pending events that are now duplicates (in useEffect, not during render)
  useEffect(() => {
    const DUPLICATE_DETECTION_WINDOW_MS = 5000;
    pendingMatchEvents.forEach(pe => {
      let hasDuplicate = false;

      // For group events, check by eventId
      if (pe.eventType === 'group_match') {
        hasDuplicate = allEvents.some(e => e.eventId === pe.eventId);
      } else {
        // For match events (known and SOI), check by person ID and time window
        // Use person_id if available (more precise), fallback to personName
        hasDuplicate = allEvents.some(e => {
          if (e.eventType !== 'match' && e.eventType !== 'soi_match') return false;

          // Match by person_id if both have it
          const pendingPersonId = pe.metadata?.person?.person_id;
          const realPersonId = e.metadata?.person?.person_id;

          if (pendingPersonId && realPersonId) {
            if (pendingPersonId !== realPersonId) return false;
          } else {
            // Fallback to personName if person_id not available
            if (e.metadata?.personName !== pe.metadata?.personName) return false;
          }

          const timeDiff = Math.abs(new Date(e.createdAt).getTime() - new Date(pe.createdAt).getTime());

          return timeDiff < DUPLICATE_DETECTION_WINDOW_MS;
        });
      }

      if (hasDuplicate) {
        removePendingMatchEvent(pe.eventId);
      }
    });
  }, [allEvents, pendingMatchEvents, removePendingMatchEvent]);

  // Settings management (from global context)
  const { deleteConfirmation, deleteConfirmationRef, isEventTypeVisible } = useSettings();

  // Filter events based on visibility settings
  const filteredEvents = React.useMemo(() => {
    const filtered = mergedEvents.filter(event => {
      // Always show events without eventType (shouldn't happen, but be defensive)
      if (!event.eventType) return true;

      // Check if this event type is visible
      const isVisible = isEventTypeVisible(event.eventType);
      if (!isVisible && event.eventType === 'soi_match') {
        console.log('[HistoryManager] SOI match event filtered out by visibility settings:', event.eventId);
      }
      return isVisible;
    });


    return filtered;
  }, [mergedEvents, isEventTypeVisible]);

  // Dialog state management
  const {
    dialogs,
    formState,
    openDialog,
    closeDialog,
    updateForm,
    closeAndResetDialog
  } = useDialogState();

  // Track expanded assignments and group matches
  const [expandedAssignments, setExpandedAssignments] = useState(new Set());
  const [expandedGroupMatches, setExpandedGroupMatches] = useState(new Set());

  // Statement creation dialog
  const [statementDialogOpen, setStatementDialogOpen] = useState(false);
  const [statementEvent, setStatementEvent] = useState(null);

  // ALWAYS keep open assignment expanded - it should never be collapsed
  useEffect(() => {
    const openAssignment = filteredEvents.find(e => e.eventType === 'assignment' && e.status === 'open');
    const openAssignmentId = openAssignment?.eventId;

    if (openAssignmentId) {
      setExpandedAssignments(prev => {
        // Only update if not already expanded
        if (prev.has(openAssignmentId)) return prev;
        const newSet = new Set(prev);
        newSet.add(openAssignmentId);
        return newSet;
      });
    }
  }, [filteredEvents]); // Run whenever events change to ensure open assignment stays expanded

  // Simple event organization - just get top-level events
  // Components handle rendering their own children
  const topLevelEvents = useSimpleEventOrganization(filteredEvents);

  // Stable ref for deleteEventById
  const deleteEventByIdRef = useRef(deleteEventById);
  useEffect(() => {
    deleteEventByIdRef.current = deleteEventById;
  }, [deleteEventById]);

  // Stable ref for loadAllEvents to avoid re-running effects
  const loadAllEventsRef = useRef(loadAllEvents);
  useEffect(() => {
    loadAllEventsRef.current = loadAllEvents;
  }, [loadAllEvents]);

  // Load events on first open only (optimistic pending events + WebSocket keep us updated)
  const hasLoadedOnce = useRef(false);
  useEffect(() => {
    if (open) {
      loadAllEventsRef.current();
      hasLoadedOnce.current = true;
    }
  }, [open]);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  /**
   * Handle assignment creation
   */
  const handleCreateAssignment = useCallback(async () => {
    const success = await createNewAssignment(formState.newAssignmentDesc);
    if (success) {
      closeAndResetDialog('createAssignment', 'newAssignmentDesc');
    }
  }, [createNewAssignment, formState.newAssignmentDesc, closeAndResetDialog]);

  /**
   * Handle assignment completion (toggle to closed status)
   */
  const handleCompleteAssignment = useCallback(async () => {
    if (!formState.selectedAssignment) return;
    const success = await toggleAssignment(formState.selectedAssignment.eventId, 'closed');
    if (success) {
      closeAndResetDialog('completeAssignment', 'selectedAssignment');
    }
  }, [toggleAssignment, formState.selectedAssignment, closeAndResetDialog]);

  /**
   * Handle note creation
   */
  const handleCreateNote = useCallback(async () => {
    const success = await createNewNote(formState.noteText);
    if (success) {
      closeAndResetDialog('createNote', 'noteText');
    }
  }, [createNewNote, formState.noteText, closeAndResetDialog]);

  /**
   * Handle event click - opens action dialog
   */
  const handleEventClick = useCallback((event) => {
    updateForm('selectedEvent', event);
    openDialog('eventAction');
  }, [updateForm, openDialog]);

  /**
   * Handle edit button click from action dialog
   */
  const handleEditClick = useCallback(() => {
    updateForm('editDescription', formState.selectedEvent.description);
    closeDialog('eventAction');
    openDialog('editEvent');
  }, [formState.selectedEvent, updateForm, closeDialog, openDialog]);

  /**
   * Handle edit save
   */
  const handleEditSave = useCallback(async () => {
    const success = await updateEventDescription(
      formState.selectedEvent.eventId,
      formState.editDescription
    );
    if (success) {
      closeAndResetDialog('editEvent', 'selectedEvent', 'editDescription');
    }
  }, [updateEventDescription, formState.selectedEvent, formState.editDescription, closeAndResetDialog]);

  /**
   * Handle delete from action dialog
   */
  const handleDeleteFromAction = useCallback(async () => {
    closeDialog('eventAction');
    const eventType = formState.selectedEvent?.eventType;

    if (deleteConfirmation[eventType]) {
      // Show confirmation dialog
      updateForm('eventToDelete', formState.selectedEvent);
      openDialog('deleteEvent');
    } else {
      // Delete immediately without confirmation
      await deleteEventById(formState.selectedEvent);
      updateForm('selectedEvent', null);
    }
  }, [
    closeDialog,
    formState.selectedEvent,
    deleteConfirmation,
    updateForm,
    openDialog,
    deleteEventById
  ]);

  /**
   * Handle delete event confirmation
   */
  const handleDeleteEvent = useCallback(async () => {
    const success = await deleteEventById(formState.eventToDelete);
    if (success) {
      closeAndResetDialog('deleteEvent', 'eventToDelete');
    }
  }, [deleteEventById, formState.eventToDelete, closeAndResetDialog]);


  /**
   * Handle delete click from list item (stable callback using refs)
   */
  const handleDeleteClick = useCallback(async (event) => {
    const eventType = event?.eventType;

    if (deleteConfirmationRef.current[eventType]) {
      // Show confirmation dialog
      updateForm('eventToDelete', event);
      openDialog('deleteEvent');
    } else {
      // Delete immediately without confirmation
      await deleteEventByIdRef.current(event);
    }
  }, [deleteConfirmationRef, updateForm, openDialog]);

  /**
   * Handle assignment expand/collapse toggle
   * IMPORTANT: Open assignments (status='open') cannot be collapsed - they must always be expanded
   */
  const handleToggleAssignment = useCallback((eventId) => {
    // Check if this is an open assignment
    const assignment = filteredEvents.find(e => e.eventId === eventId);
    const isOpenAssignment = assignment?.eventType === 'assignment' && assignment?.status === 'open';

    // Don't allow collapsing open assignments
    if (isOpenAssignment) {
      return;
    }

    setExpandedAssignments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  }, [filteredEvents]);

  /**
   * Handle group match expand/collapse toggle
   */
  const handleToggleGroupMatch = useCallback((eventId) => {
    setExpandedGroupMatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  }, []);

  /**
   * Handle manual refresh
   */
  const handleRefresh = useCallback(() => {
    loadAllEvents();
  }, [loadAllEvents]);

  /**
   * Handle page size change
   */
  const handlePageSizeChange = useCallback((newSize) => {
    changePageSize(newSize);
    // Trigger reload after page size changes
    setTimeout(() => loadAllEvents(), 0);
  }, [changePageSize, loadAllEvents]);

  // ============================================================================
  // Dialog Close Handlers
  // ============================================================================

  const dialogCloseHandlers = {
    createNote: () => closeDialog('createNote'),
    createAssignment: () => closeDialog('createAssignment'),
    completeAssignment: () => closeDialog('completeAssignment'),
    deleteEvent: () => closeDialog('deleteEvent'),
    eventAction: () => closeDialog('eventAction'),
    editEvent: () => closeDialog('editEvent')
  };

  // ============================================================================
  // Dialog Action Handlers
  // ============================================================================

  const dialogActionHandlers = {
    onNoteTextChange: (value) => updateForm('noteText', value),
    onAssignmentDescChange: (value) => updateForm('newAssignmentDesc', value),
    onSelectAssignment: (value) => updateForm('selectedAssignment', value),
    onEditDescChange: (value) => updateForm('editDescription', value),
    onCreateNote: handleCreateNote,
    onCreateAssignment: handleCreateAssignment,
    onCompleteAssignment: handleCompleteAssignment,
    onDeleteEvent: handleDeleteEvent,
    onEditClick: handleEditClick,
    onDeleteFromAction: handleDeleteFromAction,
    onEditSave: handleEditSave,
    onCreateStatement: (event) => {
      console.log('[HistoryManager] Create statement from event:', event);
      setStatementEvent(event);
      setStatementDialogOpen(true);
      closeDialog('eventAction');
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <>
      <HistoryDrawer
        open={open}
        onClose={onClose}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        error={error}
        success={success}
        clearError={clearError}
        clearSuccess={clearSuccess}
        topLevelEvents={topLevelEvents}
        allEvents={filteredEvents}
        expandedGroupMatches={expandedGroupMatches}
        openAssignments={openAssignments}
        onEventClick={handleEventClick}
        onDeleteClick={handleDeleteClick}
        onToggleGroupMatch={handleToggleGroupMatch}
        onToggleAssignmentStatus={toggleAssignment}
        onRefresh={handleRefresh}
        onLoadMore={loadMoreEvents}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        onOpenNoteDialog={() => openDialog('createNote')}
        onOpenCreateDialog={() => openDialog('createAssignment')}
        onOpenCompleteDialog={() => openDialog('completeAssignment')}
        wsConnected={websocketEnabled && wsConnected}
        onWSReconnect={connectWebSocket}
      />

      <HistoryDialogs
        dialogs={dialogs}
        formState={formState}
        loading={loading}
        allEvents={allEvents}
        openAssignments={openAssignments}
        handlers={dialogActionHandlers}
        onClose={dialogCloseHandlers}
      />

      <CreateStatementDialog
        open={statementDialogOpen}
        event={statementEvent}
        onClose={() => {
          setStatementDialogOpen(false);
          setStatementEvent(null);
        }}
        onComplete={() => {
          console.log('[HistoryManager] Statement created successfully');
        }}
      />
    </>
  );
}
